// src/pages/VolunteerManagerDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config';
import TenantUrlHelper from '../services/TenantUrlHelper';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  doc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';

const VolunteerManagerDashboard = ({ currentUser }) => {
  const [stats, setStats] = useState({
    totalVolunteers: 0,
    pendingRegistrations: 0,
    upcomingEvents: 0,
    activeAssignments: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadDashboardData();
    }
  }, [currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const tenantId = currentUser.tenantId;
      
      // Load all data in parallel
      const [
        volunteersData,
        registrationsData,
        eventsData,
        assignmentsData
      ] = await Promise.all([
        loadVolunteers(tenantId),
        loadPendingRegistrations(tenantId),
        loadUpcomingEvents(tenantId),
        loadActiveAssignments(tenantId)
      ]);
      
      // Update stats
      setStats({
        totalVolunteers: volunteersData.length,
        pendingRegistrations: registrationsData.length,
        upcomingEvents: eventsData.length,
        activeAssignments: assignmentsData.length
      });
      
      setPendingRegistrations(registrationsData.slice(0, 5)); // Show first 5
      setUpcomingEvents(eventsData.slice(0, 5)); // Show first 5
      
      // Create recent activity feed with null safety
      const activities = [
        ...registrationsData.slice(0, 3).map(reg => ({
          type: 'registration',
          message: `New volunteer registration: ${reg.firstName || 'Unknown'} ${reg.lastName || 'User'}`,
          timestamp: reg.submittedAt,
          id: reg.id
        })),
        ...eventsData.slice(0, 2).map(event => ({
          type: 'event',
          message: `Upcoming event: ${event.title || event.eventName || 'Untitled Event'}`,
          timestamp: event.date,
          id: event.id
        }))
      ].sort((a, b) => {
        const aTime = a.timestamp?.toDate() || new Date(0);
        const bTime = b.timestamp?.toDate() || new Date(0);
        return bTime - aTime;
      });
      
      setRecentActivity(activities.slice(0, 5));
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadVolunteers = async (tenantId) => {
    const q = query(
      collection(db, 'volunteers'),
      where('tenantId', '==', tenantId),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadPendingRegistrations = async (tenantId) => {
    const q = query(
      collection(db, 'volunteerRegistrations'),
      where('tenantId', '==', tenantId),
      where('status', '==', 'pending'),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadUpcomingEvents = async (tenantId) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, 'volunteerEvents'),
      where('tenantId', '==', tenantId),
      where('date', '>=', today),
      where('status', '==', 'active'),
      orderBy('date', 'asc'),
      limit(10)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadActiveAssignments = async (tenantId) => {
    const q = query(
      collection(db, 'volunteerAssignments'),
      where('tenantId', '==', tenantId),
      where('status', 'in', ['assigned', 'confirmed'])
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const quickApproveRegistration = async (registrationId) => {
    try {
      await updateDoc(doc(db, 'volunteerRegistrations', registrationId), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: currentUser.uid
      });
      
      // Refresh data
      loadDashboardData();
      
    } catch (err) {
      console.error('Error approving registration:', err);
      setError('Failed to approve registration. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <h1>Volunteer Management Dashboard</h1>
        <div style={{ padding: '40px' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>Loading dashboard...</div>
          <div style={{ color: '#6c757d' }}>Please wait while we gather your volunteer data.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header with Navigation */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0, color: '#28a745' }}>Volunteer Management Dashboard</h1>
        </div>
        
        {/* Embedded Navigation Bar */}
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '15px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', color: '#666', marginRight: '10px' }}>Quick Navigation:</span>
            
            <Link 
              to="/volunteer-events"
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                padding: '8px 16px',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <span>📅</span> Manage Events
            </Link>
            
            <Link 
              to="/volunteer-directory"
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '8px 16px',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <span>📋</span> Volunteer Directory
            </Link>
            
            <Link 
              to="/volunteer-approvals"
              style={{
                backgroundColor: '#ffc107',
                color: '#212529',
                padding: '8px 16px',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <span>✅</span> Registration Approvals
              {stats.pendingRegistrations > 0 && (
                <span style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '2px 6px',
                  fontSize: '11px',
                  marginLeft: '5px'
                }}>
                  {stats.pendingRegistrations}
                </span>
              )}
            </Link>
            
            <Link 
              to="/available-volunteer-events"
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                padding: '8px 16px',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <span>🎯</span> Available Events
            </Link>

            <Link 
              to="/admin/volunteer-registration-setup"
              style={{
                backgroundColor: '#6f42c1',
                color: 'white',
                padding: '8px 16px',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <span>⚙️</span> Form Setup
            </Link>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ 
          color: '#721c24', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px' 
        }}>
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px',
          borderLeft: '4px solid #28a745'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 5px 0', color: '#28a745' }}>Active Volunteers</h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{stats.totalVolunteers}</div>
            </div>
            <div style={{ fontSize: '32px', opacity: 0.7 }}>👥</div>
          </div>
          <Link 
            to="/volunteer-directory"
            style={{ 
              color: '#28a745', 
              fontSize: '12px', 
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            View Directory →
          </Link>
        </div>

        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px',
          borderLeft: '4px solid #ffc107'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 5px 0', color: '#ffc107' }}>Pending Registrations</h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{stats.pendingRegistrations}</div>
            </div>
            <div style={{ fontSize: '32px', opacity: 0.7 }}>📝</div>
          </div>
          {stats.pendingRegistrations > 0 && (
            <Link 
              to="/volunteer-approvals"
              style={{ 
                color: '#ffc107', 
                fontSize: '12px', 
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >
              Review Now →
            </Link>
          )}
        </div>

        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px',
          borderLeft: '4px solid #007bff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 5px 0', color: '#007bff' }}>Upcoming Events</h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{stats.upcomingEvents}</div>
            </div>
            <div style={{ fontSize: '32px', opacity: 0.7 }}>📅</div>
          </div>
          <Link 
            to="/volunteer-events"
            style={{ 
              color: '#007bff', 
              fontSize: '12px', 
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            Manage Events →
          </Link>
        </div>

        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px',
          borderLeft: '4px solid #17a2b8'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 5px 0', color: '#17a2b8' }}>Active Assignments</h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{stats.activeAssignments}</div>
            </div>
            <div style={{ fontSize: '32px', opacity: 0.7 }}>🎯</div>
          </div>
          <Link 
            to="/available-volunteer-events"
            style={{ 
              color: '#17a2b8', 
              fontSize: '12px', 
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            View Assignments →
          </Link>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        
        {/* Recent Activity */}
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '1px solid #dee2e6', paddingBottom: '10px' }}>
            Recent Activity
          </h3>
          {recentActivity.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}>📭</div>
              <p>No recent activity</p>
              <p style={{ fontSize: '14px' }}>Activity will appear here as volunteers register and events are created.</p>
            </div>
          ) : (
            <div>
              {recentActivity.map((activity, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '15px',
                  padding: '10px 0',
                  borderBottom: index < recentActivity.length - 1 ? '1px solid #f8f9fa' : 'none'
                }}>
                  <div style={{ fontSize: '20px' }}>
                    {activity.type === 'registration' ? '👤' : '📅'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#333' }}>{activity.message}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {formatDateTime(activity.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending Registrations */}
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#333' }}>Pending Registrations</h3>
            {pendingRegistrations.length > 0 && (
              <Link 
                to="/volunteer-approvals"
                style={{ color: '#007bff', fontSize: '14px', textDecoration: 'none' }}
              >
                View All →
              </Link>
            )}
          </div>
          
          {pendingRegistrations.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}>✅</div>
              <p>No pending registrations</p>
              <p style={{ fontSize: '14px' }}>All volunteer registrations have been processed.</p>
            </div>
          ) : (
            <div>
              {pendingRegistrations.map((registration) => (
                <div key={registration.id} style={{ 
                  border: '1px solid #f8f9fa',
                  borderRadius: '6px',
                  padding: '15px',
                  marginBottom: '10px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#333' }}>
                        {registration.firstName || 'Unknown'} {registration.lastName || 'User'}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '2px' }}>
                        {registration.email || 'No email provided'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                        Submitted: {formatDate(registration.submittedAt)}
                      </div>
                    </div>
                    <button
                      onClick={() => quickApproveRegistration(registration.id)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Quick Approve
                    </button>
                  </div>
                  {registration.skills && registration.skills.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                      <strong>Skills:</strong> {Array.isArray(registration.skills) ? registration.skills.join(', ') : registration.skills}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Events */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>Upcoming Events</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Link 
              to="/volunteer-events"
              style={{ color: '#007bff', fontSize: '14px', textDecoration: 'none' }}
            >
              Manage Events →
            </Link>
            <Link 
              to="/volunteer-events"
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '6px 12px',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              + Create Event
            </Link>
          </div>
        </div>
        
        {upcomingEvents.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6c757d', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}>📅</div>
            <p>No upcoming events scheduled</p>
            <p style={{ fontSize: '14px' }}>Create your first volunteer event to get started.</p>
            <Link 
              to="/volunteer-events"
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                padding: '10px 20px',
                textDecoration: 'none',
                borderRadius: '6px',
                display: 'inline-block',
                marginTop: '10px'
              }}
            >
              Create First Event
            </Link>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
            {upcomingEvents.map((event) => (
              <div key={event.id} style={{ 
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                      {event.title || event.eventName || 'Untitled Event'}
                    </h4>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      {formatDate(event.date)} {event.startTime && `at ${event.startTime}`}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      {event.location || 'Location TBD'}
                    </div>
                  </div>
                  <div style={{ 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '12px', 
                    fontSize: '12px' 
                  }}>
                    {event.volunteersNeeded || event.requiredVolunteers || 0} needed
                  </div>
                </div>
                {event.description && (
                  <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
                    {event.description.substring(0, 100)}
                    {event.description.length > 100 ? '...' : ''}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '5px' }}>
                  <Link 
                    to={`/volunteer-events`}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: '6px 12px',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    View Details
                  </Link>
                  <Link 
                    to={`/volunteer-directory`}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '6px 12px',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    Assign Volunteers
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginTop: '20px' }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Quick Actions</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <Link 
            to="/volunteer-events"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              border: '2px solid #007bff',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#007bff',
              backgroundColor: '#f8f9fa',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '24px' }}>📅</div>
            <div>
              <div style={{ fontWeight: 'bold' }}>Create Event</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Schedule new volunteer opportunity</div>
            </div>
          </Link>

          <Link 
            to="/volunteer-directory"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              border: '2px solid #28a745',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#28a745',
              backgroundColor: '#f8f9fa',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '24px' }}>👥</div>
            <div>
              <div style={{ fontWeight: 'bold' }}>Browse Volunteers</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>View volunteer directory</div>
            </div>
          </Link>

          <Link 
            to="/volunteer-approvals"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#ffc107',
              backgroundColor: '#f8f9fa',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '24px' }}>✅</div>
            <div>
              <div style={{ fontWeight: 'bold' }}>Review Registrations</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                {stats.pendingRegistrations > 0 ? `${stats.pendingRegistrations} pending` : 'All caught up!'}
              </div>
            </div>
          </Link>

          <Link 
            to="/available-volunteer-events"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              border: '2px solid #17a2b8',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#17a2b8',
              backgroundColor: '#f8f9fa',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '24px' }}>🎯</div>
            <div>
              <div style={{ fontWeight: 'bold' }}>Available Events</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>View volunteer assignments</div>
            </div>
          </Link>

          <Link 
            to="/admin/volunteer-registration-setup"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              border: '2px solid #6f42c1',
              borderRadius: '8px',
              textDecoration: 'none',
              color: '#6f42c1',
              backgroundColor: '#f8f9fa',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: '24px' }}>⚙️</div>
            <div>
              <div style={{ fontWeight: 'bold' }}>Form Setup</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Configure registration form</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Help Section */}
      <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '8px', padding: '20px', marginTop: '20px' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#004085' }}>Getting Started with Volunteer Management</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', fontSize: '14px', color: '#004085' }}>
          <div>
            <strong>1. Review Registrations:</strong> Approve or deny volunteer applications from the public registration form.
          </div>
          <div>
            <strong>2. Create Events:</strong> Schedule volunteer opportunities with specific skills and time requirements.
          </div>
          <div>
            <strong>3. Assign Volunteers:</strong> Match volunteers to events based on their skills and availability.
          </div>
          <div>
            <strong>4. Monitor Engagement:</strong> Track volunteer participation and send automated reminders.
          </div>
        </div>
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#004085' }}>
            <strong>Public Registration URL:</strong> 
            <code style={{ backgroundColor: '#f8f9fa', padding: '2px 6px', marginLeft: '5px' }}>
              {TenantUrlHelper.getPublicRegistrationUrl('volunteer', window.location.hostname.split('.')[0])}
            </code>
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#6c757d' }}>
            Share this URL with potential volunteers.
            {TenantUrlHelper.isDevelopment() ? ' (Development URL - will be clean in production)' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VolunteerManagerDashboard;